import { Component, OnInit, ElementRef, ViewChild, AfterViewInit, OnDestroy, Input } from "@angular/core";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { AuxiliaryObjectType } from "~/src/utils/shape-type";
import { SnackBarConfig } from "../constants/snackbar.constants";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ZoomConfig } from "@utils/canvas-configuration";
import { CanvasConfig } from "../../../../utils/canvas-configuration";

@Component({
    selector: "app-viewer360",
    templateUrl: "./viewer360.component.html",
    styleUrls: ["./viewer360.component.scss"],
    standalone: false,
})
export class Viewer360Component implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild("canvas") private canvasRef!: ElementRef;
    @Input() glbURL!: string;
    public isLoading = false;

    private scene!: THREE.Scene;
    private camera!: THREE.PerspectiveCamera;
    public zoomConfig = ZoomConfig;
    private renderer!: THREE.WebGLRenderer;
    private controls: any;
    private model: THREE.Group | undefined;
    private resizeListener: () => void; // Store the resize listener for later removal
    public glbUrl: string = "";

    constructor(private snackBar: MatSnackBar) {
        this.resizeListener = this.resizeCanvasToDisplaySize.bind(this); // Bind this to the method
    }

    ngOnInit(): void {
        this.isLoading = true;
    }

    ngAfterViewInit(): void {
        this.initThreeJs();
        const canvas = this.canvasRef.nativeElement;
        canvas.addEventListener("wheel", this.onMouseWheel.bind(this));
    }

    private initThreeJs(): void {
        if (!this.canvasRef || !this.canvasRef.nativeElement) return;

        this.scene = new THREE.Scene();
        const container = this.canvasRef.nativeElement.parentElement;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        this.camera = new THREE.PerspectiveCamera(75, containerWidth / containerHeight, 0.1, 10000);
        this.camera.position.set(650, 650, 650);

        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvasRef.nativeElement,
            antialias: true,
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(containerWidth, containerHeight, false);
        this.renderer.setClearColor(0x000000, 0);
        this.isLoading = true;
        const loader = new GLTFLoader();

        loader.load(
            this.glbURL,
            (gltf) => {
                this.model = gltf.scene;
                // Remove any unwanted planes in the model
                this.model.traverse((child) => {
                    if (child.name.includes(AuxiliaryObjectType.Plane)) {
                        child.visible = false;
                    }
                });
                this.scene.add(this.model);
                this.isLoading = false;
            },
            undefined,
            (error) => {
                console.error("Error loading .glb model", error);
                this.isLoading = false;
            },
        );
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableZoom = true;
        this.controls.enablePan = false;
        this.controls.minDistance = 150;
        this.controls.maxDistance = 1500;
        this.controls.maxPolarAngle = Math.PI / 2;
        this.animate();
    }

    private resizeCanvasToDisplaySize(): void {
        const container = this.canvasRef.nativeElement.parentElement;
        const width = container.clientWidth;
        const height = container.clientHeight;
        this.renderer.setSize(width, height, false);
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
    }

    private animate(): void {
        requestAnimationFrame(() => this.animate());
        this.resizeCanvasToDisplaySize();
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }

    private onMouseWheel = (event: WheelEvent) => {
        if (event.deltaY < 0) {
            this.handleMouseZoom(-CanvasConfig.zoomDefaultValue);
        } else {
            this.handleMouseZoom(CanvasConfig.zoomDefaultValue);
        }
    };

    public zoom(direction: string): void {
        switch (direction) {
            case this.zoomConfig.zoomIn:
                this.handleClickZoom(-CanvasConfig.zoomDefaultValue);
                break;
            case this.zoomConfig.zoomOut:
                this.handleClickZoom(CanvasConfig.zoomDefaultValue);
                break;
        }
    }
    // Zoom in/out function using mouse click
    public handleClickZoom(zoomChange: number): void {
        this.zoomCamera(zoomChange, true);
    }

    // Zoom in/out function using mouse wheel
    private handleMouseZoom(distanceChange: number): void {
        this.zoomCamera(distanceChange, false);
    }

    private zoomCamera(distanceDelta: number, isZoomByClick: boolean): void {
        const currentDistance = Math.round(this.camera.position.distanceTo(this.controls.target));
        let newDistance = Math.max(
            this.controls.minDistance,
            Math.min(currentDistance + distanceDelta, this.controls.maxDistance),
        );
        const isZoomInOut = distanceDelta < 0;
        const isOutOfBound =
            (currentDistance === this.controls.minDistance && distanceDelta < 0) ||
            (currentDistance === this.controls.maxDistance && distanceDelta > 0);

        if (isOutOfBound) {
            this.showZoomLimitMessage(isZoomInOut);
        } else {
            this.controls.enableZoom = true;
        }
        const clickDelta = newDistance - currentDistance;
        if (isZoomByClick) {
            const direction = new THREE.Vector3().subVectors(this.camera.position, this.controls.target).normalize();
            direction.multiplyScalar(clickDelta);
            this.camera.position.add(direction);
            this.camera.updateProjectionMatrix();
        }
    }

    private showZoomLimitMessage(isZoomIn: boolean): void {
        const text = isZoomIn ? "in" : "out";
        const template = this.zoomConfig.zoomLimitMessage;
        const message = template.replace("${direction}", text);
        this.controls.enableZoom = false;
        this.snackBar.open(message, SnackBarConfig.Actions.CLOSE, {
            duration: SnackBarConfig.Duration.MEDIUM,
        });
    }

    ngOnDestroy(): void {
        window.removeEventListener("resize", this.resizeListener);
        this.canvasRef.nativeElement.removeEventListener("wheel", this.onMouseWheel);
    }
}
